import { createContext, useContext, useEffect, useState } from "react";
import api from "../api/client";
import { applyUserTimezone } from "../utils";

const AuthCtx = createContext(null);

// Fallback used before the permissions config loads (mirrors backend defaults).
// Debe COINCIDIR con el default del backend (appconfig DEFAULTS.permissions).
// Es solo un fallback si /config/permissions no cargó; si difiere, un miembro
// puede quedarse sin lo que sí le toca (p. ej. ver los proyectos de su división).
const DEFAULT_CAPS = {
  department_board: ["member", "supervisor", "manager", "admin"],
  dashboard: ["supervisor", "manager", "admin", "directivo", "pmo"],
  metrics: ["supervisor", "manager", "admin", "directivo", "pmo"],
  bitacora: ["supervisor", "manager", "admin"],
  export_data: ["manager", "admin", "directivo", "pmo"],
  send_reports: ["manager", "admin", "directivo"],
  import_excel: ["manager", "admin"],
  manage_departments: ["manager", "admin"],
  manage_users: ["admin"],
  manage_config: ["admin"],
  documents: ["member", "supervisor", "manager", "admin"],
  manage_documents: ["supervisor", "manager", "admin"],
  manage_contacts: ["supervisor", "manager", "admin"],
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("tf_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState(null); // null = not loaded yet
  const [features, setFeatures] = useState({});
  // Reportes concedidos puntualmente al usuario (claves del catálogo). Permiten
  // ver un reporte suelto aunque no se tenga la capacidad base.
  const [reportGrants, setReportGrants] = useState([]);
  // Módulos concedidos puntualmente al usuario (claves de sección). Permiten ver
  // un módulo aunque el rol no lo tenga (análogo a reportGrants).
  const [moduloGrants, setModuloGrants] = useState([]);
  // Módulos QUITADOS puntualmente al usuario (claves de sección), aunque su rol
  // se los dé. Es la única excepción "resta" del sistema: se revisa antes que
  // todo lo demás en can().
  const [moduloDenies, setModuloDenies] = useState([]);
  // Capacidades SUELTAS concedidas puntualmente (CapGrant) — p. ej.
  // compras_directa, soporte_admin. El backend ya las exige vía has_capability();
  // sin esto `can()` nunca las ve y el usuario se queda sin poder llegar a usar
  // lo que sí tiene permitido (menú oculto, ruta bloqueada).
  const [capGrants, setCapGrants] = useState([]);
  // ¿Ya terminó de cargar la config de permisos (matriz + features + grants)?
  // Hasta que sea true, Protected NO debe expulsar por falta de capacidad: el
  // acceso de un miembro por concesión llega async y expulsarlo antes lo deja
  // fuera aunque tenga el permiso.
  const [permsReady, setPermsReady] = useState(false);
  // Permisos efectivos resueltos por el backend (GET /config/mi-perfil): caps,
  // readonly y, si tiene perfil asignado, el nivel de cada ruta. Es la fuente
  // única de can()/readonly/nivelRuta (Fase 4); null = aún no cargó o falló.
  const [miPerfil, setMiPerfil] = useState(null);

  // Multi-país: cuando hay usuario, las horas del servidor se muestran en SU
  // zona horaria (fallback: la del navegador).
  useEffect(() => { applyUserTimezone(user?.timezone); }, [user?.timezone]);

  const loadPermissions = () => {
    if (localStorage.getItem("tf_token")) {
      // Cargar matriz + features + concesiones de reportes; marcar listo cuando
      // TODAS resuelvan (aunque alguna falle) para no bloquear la navegación.
      Promise.allSettled([
        api.get("/config/permissions").then((r) => setPermissions(r.data.permissions || {})),
        api.get("/config/features").then((r) => setFeatures(r.data || {})),
        api.get("/config/mis-reportes").then((r) => setReportGrants(r.data.reportes || [])),
        api.get("/config/mis-modulos").then((r) => setModuloGrants(r.data.modulos || [])),
        api.get("/config/mis-caps").then((r) => setCapGrants(r.data.caps || [])),
        api.get("/config/mis-modulos-denegados").then((r) => setModuloDenies(r.data.modulos || [])),
        api.get("/config/mi-perfil").then((r) => setMiPerfil(r.data?.caps ? r.data : null)),
      ]).finally(() => setPermsReady(true));
    } else {
      setPermsReady(true);
    }
  };

  useEffect(() => {
    // Refresh profile + permissions on mount if we have a token.
    if (localStorage.getItem("tf_token")) {
      api.get("/auth/me").then((r) => {
        setUser(r.data);
        localStorage.setItem("tf_user", JSON.stringify(r.data));
      }).catch(() => {});
      loadPermissions();
    }
  }, []);

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    localStorage.setItem("tf_user", JSON.stringify(data));
    return data;
  };

  const login = async (email, password) => {
    setLoading(true);
    try {
      const form = new URLSearchParams();
      form.append("username", email);
      form.append("password", password);
      const { data } = await api.post("/auth/login", form);
      localStorage.setItem("tf_token", data.access_token);
      localStorage.setItem("tf_user", JSON.stringify(data.user));
      setUser(data.user);
      loadPermissions();
      return data.user;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("tf_token");
    localStorage.removeItem("tf_user");
    setMiPerfil(null);
    setUser(null);
  };

  const roleAtLeast = (min) => {
    const order = { member: 1, pmo: 1, supervisor: 2, manager: 3, admin: 4 };
    if (!user) return false;
    const primary = order[user.role] || 0;
    const secondary = user.secondary_role ? (order[user.secondary_role] || 0) : 0;
    return Math.max(primary, secondary) >= order[min];
  };

  // cap "ver" de módulo → clave de sección (para la concesión por usuario).
  const MODULO_GRANT_CAP = {
    itam: "itam", soporte: "soporte", compras: "compras", documents: "documentacion",
    manage_contacts: "directorio", materiales: "materiales", mensajeria: "mensajeria",
    levantamiento: "levantamiento", dashboard: "reportes", tiendas: "tiendas",
  };
  const can = (cap) => {
    if (!user) return false;
    if (user.role === "admin" && cap === "manage_config") return true;
    // Fuente única (Fase 4): el backend ya resolvió esta cap para ESTE usuario
    // (rol + puente de perfiles + perfil asignado + concesiones/negaciones), con
    // la misma función que protege la API. Lo de abajo es solo el respaldo por
    // si /config/mi-perfil no respondió o la cap no está en su lista.
    if (miPerfil?.caps && cap in miPerfil.caps) return !!miPerfil.caps[cap];
    // Negación puntual: la única excepción "resta" del sistema, gana sobre
    // cualquier concesión por rol o por usuario. El Admin nunca se ve afectado.
    if (user.role !== "admin" && MODULO_GRANT_CAP[cap] && moduloDenies.includes(MODULO_GRANT_CAP[cap])) return false;
    const map = permissions || DEFAULT_CAPS;
    const allowed = map[cap] ?? DEFAULT_CAPS[cap];
    const roles = [user.role];
    if (user.secondary_role) roles.push(user.secondary_role);
    if (allowed && roles.some((r) => allowed.includes(r))) return true;
    // Concesión puntual de módulo: el usuario tiene concedida esa sección.
    if (MODULO_GRANT_CAP[cap] && moduloGrants.includes(MODULO_GRANT_CAP[cap])) return true;
    // Concesión puntual de capacidad suelta (CapGrant) — p. ej. compras_directa.
    if (capGrants.includes(cap)) return true;
    if (!allowed) return user.role === "admin";
    return false;
  };

  const readonlyRol = user?.role === "directivo" || user?.role === "country_manager"
    || (user?.role === "pmo" && !user?.secondary_role);
  // Lo decide el backend (perfiles.es_solo_lectura); `readonlyRol` es el respaldo.
  const readonly = typeof miPerfil?.readonly === "boolean" ? miPerfil.readonly : readonlyRol;

  // Nivel del perfil para una ruta del frontend (0 Oculto … 3 Admin), o null si
  // el usuario no tiene perfil de árbol o la ruta no está en el árbol. "/" (Mis
  // tareas) solo coincide exacto; el resto por prefijo más largo.
  const nivelRuta = (path) => {
    const rutas = miPerfil?.rutas;
    if (!rutas?.length || !path) return null;
    let mejor = null, largo = -1;
    for (const { prefijo, nivel } of rutas) {
      const ok = prefijo === "/" ? path === "/" : (path === prefijo || path.startsWith(prefijo + "/"));
      if (ok && prefijo.length > largo) { mejor = nivel; largo = prefijo.length; }
    }
    return mejor;
  };

  // ¿Se le concedió puntualmente este reporte (clave del catálogo)?
  const hasReport = (key) => !!key && reportGrants.includes(key);

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, roleAtLeast, can, readonly, permissions, loadPermissions, refreshUser, features, reportGrants, hasReport, moduloGrants, moduloDenies, capGrants, permsReady, miPerfil, nivelRuta }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
